import React from 'react';

import { FormLayout } from '@/components/layout';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import { NumericSpinner } from '@/components/ui/inputs/NumericSpinner';
import { DatePicker } from '@/components/ui/filters';
import { Selector } from '@/components/ui/inputs/selector/Selector';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import { useTranslation } from '@/i18n';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import type { CreateShiftDescriptionFormSubset } from '@/schemas/shift/createShiftDescriptionForm';
import { DtResolutionStatusBanners } from './DtResolutionStatusBanners';


interface CreateShiftDescriptionFormProps {
  form: CreateShiftDescriptionFormSubset;
  workingDayTypeOptions: SelectorOption[];
  workingDayOptions: SelectorOption[];
  isDistributedCyclesType: boolean;
  isExceptionalType: boolean;
  isStaticType: boolean;
  weeksCountEditable: boolean;
  ordinaryHours: number | null;
}

const handleSubmit = (event: React.FormEvent) => {
  event.preventDefault();
};

export const CreateShiftDescriptionForm: React.FC<CreateShiftDescriptionFormProps> = ({
  form,
  workingDayTypeOptions,
  workingDayOptions,
  isDistributedCyclesType,
  isExceptionalType,
  isStaticType,
  weeksCountEditable,
  ordinaryHours,
}) => {
  const { t: translate } = useTranslation('schedule');
  const { register, watch, setValue, errors } = form;

  const showDtAuthorizationFields =
    isExceptionalType || (isDistributedCyclesType && watch('isExceptionalWorkingDay'));

  return (
    <FormLayout onSubmit={handleSubmit} columns={4} data-testid="create-shift-description-form">
      <FormLayout.Section title={translate('shift.create.sections.description')}>
        <Selector
          label={translate('shift.create.fields.workingDayType')}
          required
          options={workingDayTypeOptions}
          value={watch('workingDayTypeId') != null ? String(watch('workingDayTypeId')) : undefined}
          onChange={(value) => {
            setValue('workingDayTypeId', normalizeNumericSelectValue(value) ?? null, { shouldDirty: true, shouldValidate: true });
            setValue('workingDayId', null, { shouldDirty: true });
          }}
          color={errors.workingDayTypeId ? 'error' : 'default'}
          supportingText={
            errors.workingDayTypeId?.message
              ? translate(errors.workingDayTypeId.message)
              : undefined
          }
          data-testid="shift-working-day-type-selector"
        />

        <Selector
          label={translate('shift.create.fields.workingDay')}
          required
          options={workingDayOptions}
          value={watch('workingDayId') != null ? String(watch('workingDayId')) : undefined}
          onChange={(value) => {
            setValue('workingDayId', normalizeNumericSelectValue(value) ?? null, { shouldDirty: true, shouldValidate: true });
          }}
          color={errors.workingDayId ? 'error' : 'default'}
          supportingText={
            errors.workingDayId?.message ? translate(errors.workingDayId.message) : undefined
          }
          data-testid="shift-working-day-selector"
        />

        <LabelInput
          label={translate('shift.create.fields.ordinaryHours')}
          value={ordinaryHours !== null ? String(ordinaryHours) : ''}
          readOnly
          data-testid="shift-ordinary-hours-input"
        />

        <NumericSpinner
          label={translate('shift.create.fields.weeksCount')}
          value={watch('weeksCount') ?? 1}
          onChange={(value) => setValue('weeksCount', value, { shouldDirty: true, shouldValidate: true })}
          min={1}
          padLength={0}
          disabled={!weeksCountEditable}
          data-testid="shift-weeks-count-spinner"
        />

        <LabelInput
          label={translate('shift.create.fields.shiftCode')}
          required
          registration={register('shiftCode')}
          color={errors.shiftCode ? 'error' : 'default'}
          supportingText={
            errors.shiftCode?.message ? translate(errors.shiftCode.message) : undefined
          }
          data-testid="shift-code-input"
        />

        <LabelInput
          label={translate('shift.create.fields.shiftName')}
          required
          registration={register('shiftName')}
          color={errors.shiftName ? 'error' : 'default'}
          supportingText={
            errors.shiftName?.message ? translate(errors.shiftName.message) : undefined
          }
          data-testid="shift-name-input"
        />

        {isStaticType && (
          <DatePicker
            label={translate('shift.create.fields.staticStartDate')}
            required
            value={watch('staticStartDate')}
            onChange={(date) => setValue('staticStartDate', date, { shouldDirty: true, shouldValidate: true })}
            color={errors.staticStartDate ? 'error' : 'default'}
            supportingText={
              errors.staticStartDate?.message
                ? translate(errors.staticStartDate.message)
                : undefined
            }
            data-testid="shift-static-start-date-picker"
          />
        )}

        {isDistributedCyclesType && (
          <Checkbox
            label={translate('shift.create.fields.isExceptionalWorkingDay')}
            checked={watch('isExceptionalWorkingDay')}
            onChange={(checked) => setValue('isExceptionalWorkingDay', checked, { shouldDirty: true, shouldValidate: true })}
            data-testid="shift-is-exceptional-checkbox"
          />
        )}

        {showDtAuthorizationFields && (
          <>
            <LabelInput
              label={translate('shift.create.fields.dtAuthorizationNumber')}
              required
              registration={register('dtAuthorizationNumber')}
              color={errors.dtAuthorizationNumber ? 'error' : 'default'}
              supportingText={
                errors.dtAuthorizationNumber?.message
                  ? translate(errors.dtAuthorizationNumber.message)
                  : undefined
              }
              data-testid="shift-dt-authorization-number-input"
            />

            <DatePicker
              label={translate('shift.create.fields.dtAuthorizationDate')}
              required
              value={watch('dtAuthorizationDate')}
              onChange={(date) => setValue('dtAuthorizationDate', date, { shouldDirty: true, shouldValidate: true })}
              color={errors.dtAuthorizationDate ? 'error' : 'default'}
              supportingText={
                errors.dtAuthorizationDate?.message
                  ? translate(errors.dtAuthorizationDate.message)
                  : undefined
              }
              data-testid="shift-dt-authorization-date-picker"
            />

            <DtResolutionStatusBanners dtAuthorizationDate={watch('dtAuthorizationDate')} />
          </>
        )}
      </FormLayout.Section>
    </FormLayout>
  );
};
